import { FC, useEffect, useRef, useState } from "react";
import { toJS } from "mobx";
import { Controller } from "react-hook-form";
import { observer } from "mobx-react-lite";
import classnames from "classnames";
import { Button } from "antd";
import moment from "moment";
import locale from "antd/es/date-picker/locale/ru_RU";

import defectFilterFieldStore from "../../store/defectFilterFields";
import dictionaryStore from "../../store/dictionary";
import userSettingsStore from "../../store/userSettings";

import { FilterCondition } from "../../constants/Enums";
import { FiltersIco } from "../../assets/customIcons";
import {
  FormInterval,
  FormField,
  IDatePicker,
  ITooltip,
  ICheckbox,
  ISelect,
  IButton,
  DraggableModal,
  IRangePicker,
  FieldsFilterSelector,
} from "../../ui-kit";
import { useOnClickOutside } from "../../hooks/useOutsideClick";
import { useHandleForm } from "../../hooks/useHandleForm";

import styles from "./Filter.module.scss";
import nsi from "../../store/nsi";
import Constants from "../../constants/Common";
import { isArray, isNull } from "lodash";
import { useCachedFilters } from "../../hooks/useCachedFilters";

const DEFAULT_VALUES = {
  dateTimeDisable: null,
  dateTimeEnable: null,
  externalId: null,
  gridTypeId: null,
  organizationId: null,
  id: null,
  workStatusId: null,
  districtId: null,
  restrictionNatureId: null,
  repairCategoryId: null,
  addressText: null,
  mediaCoverage: null,
  overBuildings: {
    value: null,
    valueTo: null,
    conditionType: FilterCondition.Equal,
  },
  livingBuildings: {
    value: null,
    valueTo: null,
    conditionType: FilterCondition.Equal,
  },
  hospitalBuildings: {
    value: null,
    valueTo: null,
    conditionType: FilterCondition.Equal,
  },
  childrenBuildings: {
    value: null,
    valueTo: null,
    conditionType: FilterCondition.Equal,
  },
  diameter: {
    value: null,
    valueTo: null,
    conditionType: FilterCondition.Equal,
  },
  disconnectedSubscribers: {
    value: null,
    valueTo: null,
    conditionType: FilterCondition.Equal,
  },
};

const DEFAULT_SETTING_NAME = "defectFilter";

const DefectFilter: FC = observer(({}) => {
  const {
    titleIcons: { settingsFilter },
  } = Constants;
  const [openFilters, setOpenFilters] = useState(false);
  const [updateKey, setUpdateKey] = useState(0);

  const { value: cachedFilters, saveFilters } = useCachedFilters("reestr");

  const {
    register,
    isFocused,
    setIsFocused,
    handleBlur,
    handleFocus,
    handleSubmit,
    reset,
    errors,
    watchAllField,
    control,
    setValue,
    getValues,
    formState,
  } = useHandleForm(null, DEFAULT_VALUES, null);

  useEffect(() => {
    if (cachedFilters) {
      Object.entries(cachedFilters).forEach(([key, value]) => {
        if (key.indexOf("date") > -1) {
          if (isArray(value)) {
            value = value.map((x) => moment(x));
          }
        }

        setValue(key, value);
      });
      applyFilter(getValues());
    }
  }, [cachedFilters]);

  const changeGridType = async function (val) {
    setValue("gridTypeId", val);
    await nsi.getOrganizations(val);
    setUpdateKey(updateKey + 1);
  };

  const mainFields = [
    {
      component: ({ field }) => {
        return (
          <IRangePicker
            field={field}
            locale={locale}
            format="DD.MM.YYYY"
            label="Дата отключения/ограничения"
          />
        );
      },
      name: "dateTimeDisable",
      placeholder: "Дата отключения/ограничения",
    },
    {
      component: ({ field }) => {
        return (
          <IRangePicker
            field={field}
            locale={locale}
            format="DD.MM.YYYY"
            label="Дата устранения дефекта"
          />
        );
      },
      name: "dateTimeEnable",
      placeholder: "Дата устранения дефекта",
    },
    {
      component: ({ field }) => {
        return (
          <ISelect
            field={field}
            optionsArr={nsi.gridTypes}
            placeholder="Тип сети"
            disabled={false}
            onChange={changeGridType}
          />
        );
      },
      name: "gridTypeId",
      placeholder: "Тип сети",
    },
    {
      component: ({ field }) => (
        <ISelect
          field={field}
          optionsArr={nsi.organizations}
          placeholder="Ресурсоснабжающая организация"
          disabled={false}
        />
      ),
      name: "organizationId",
      placeholder: "Ресурсоснабжающая организация",
    },
  ];
  const fields = [
    {
      component: () => {
        return (
          <FormField
            type="number"
            name="externalId"
            label="ID дефекта"
            register={register}
            currentValue={watchAllField.externalId}
          />
        );
      },
      name: "externalId",
      placeholder: "ID дефекта",
    },
    {
      component: () => {
        return (
          <FormField
            type="text"
            name="uniqueIdentifier"
            label="GUID"
            register={register}
            currentValue={watchAllField.uniqueIdentifier}
          />
        );
      },
      name: "uniqueIdentifier",
      placeholder: "GUID",
      conditionType: FilterCondition.Like,
    },
    {
      component: ({ field }) => {
        return (
          <ISelect
            field={field}
            optionsArr={dictionaryStore.data["IncidentStatus"]}
            placeholder="Статус"
            disabled={false}
          />
        );
      },
      name: "incidentStatusId",
      placeholder: "Статус",
    },
    {
      component: ({ field }) => {
        return (
          <ISelect
            field={field}
            optionsArr={dictionaryStore.data["District"]}
            placeholder="Административный район"
            disabled={false}
          />
        );
      },
      name: "districtId",
      placeholder: "Административный район",
    },
    {
      component: ({ field }) => {
        return (
          <ISelect
            field={field}
            optionsArr={(dictionaryStore.data["RestrictionNature"] || []).map(
              (item) => ({
                ...item,
                description: item.title,
                title: item.description,
              }),
            )}
            placeholder="Характер ограничения"
            disabled={false}
          />
        );
      },
      name: "defNatureId",
      placeholder: "Характер ограничения",
    },
    {
      component: ({ field }) => {
        return (
          <ISelect
            field={field}
            optionsArr={dictionaryStore.data["RepairCategory"]}
            placeholder="Тип работ"
            disabled={false}
          />
        );
      },
      name: "repairCategoryId",
      placeholder: "Тип работ",
    },
    {
      component: () => {
        return (
          <FormField
            type="text"
            name="addressText"
            label="Адрес"
            register={register}
            currentValue={watchAllField.addressText}
          />
        );
      },
      name: "addressText",
      placeholder: "Адрес",
      conditionType: FilterCondition.Like,
    },
    {
      component: () => {
        return (
          <FormInterval
            register={register}
            control={control}
            name="overBuildings"
            currentValue={watchAllField.overBuildings}
            label="Всего зданий"
          />
        );
      },
      name: "overBuildings",
      placeholder: "Всего зданий",
    },
    {
      component: () => {
        return (
          <FormInterval
            register={register}
            control={control}
            name="livingBuildings"
            currentValue={watchAllField.livingBuildings}
            label="Жилых"
          />
        );
      },
      name: "livingBuildings",
      placeholder: "Жилых",
    },
    {
      component: () => {
        return (
          <FormInterval
            register={register}
            control={control}
            name="hospitalBuildings"
            currentValue={watchAllField.hospitalBuildings}
            label="Лечебных"
          />
        );
      },
      name: "hospitalBuildings",
      placeholder: "Лечебных",
    },
    {
      component: () => {
        return (
          <FormInterval
            register={register}
            control={control}
            name="childrenBuildings"
            currentValue={watchAllField.childrenBuildings}
            label="Детских"
          />
        );
      },
      name: "childrenBuildings",
      placeholder: "Детских",
    },
    {
      component: () => {
        return (
          <FormInterval
            register={register}
            control={control}
            name="diameter"
            currentValue={watchAllField.diameter}
            label="Диаметр трубы"
          />
        );
      },
      name: "diameter",
      placeholder: "Диаметр трубы",
    },
    {
      component: () => {
        return (
          <FormInterval
            register={register}
            control={control}
            name="disconnectedSubscribers"
            currentValue={watchAllField.disconnectedSubscribers}
            label="Кол. откл. абонентов"
          />
        );
      },
      name: "disconnectedSubscribers",
      placeholder: "Количество отключенных абонентов",
    },
    {
      component: ({ field: { value } }) => {
        return (
          <ICheckbox
            checked={value}
            label="Признак СМИ"
            onChange={
              isNull(value)
                ? setValue("mediaCoverage", false)
                : (e) => {
                    setValue("mediaCoverage", e.target.checked);
                  }
            }
          />
        );
      },
      name: "mediaCoverage",
      placeholder: "Признак СМИ",
    },
    {
      component: ({ field }) => {
        return (
          <ISelect
            field={field}
            optionsArr={dictionaryStore.data["MkaStatus"]}
            placeholder="Признак выгрузки МКА ЖКХ"
            disabled={false}
          />
        );
      },
      name: "mkaStatusId",
      placeholder: "Признак выгрузки МКА ЖКХ",
    },
    {
      component: () => {
        return (
          <ICheckbox
            checked={watchAllField.deadlineViolated}
            label="Нарушен срок устранения"
            onChange={(e) => {
              setValue("deadlineViolated", e.target.checked);
            }}
          />
        );
      },
      name: "deadlineViolated",
      placeholder: "Нарушен срок устранения",
    },
  ];
  const groupFields = () => {
    const tempArray = [];
    const nonFixed = fields.filter((filterField) =>
      defectFilterFieldStore.visibleFieldsKeys?.keys.includes(filterField.name),
    );
    const chunkSize = 4;
    for (let i = 0; i < nonFixed.length; i += chunkSize) {
      tempArray.push(nonFixed.slice(i, i + chunkSize));
    }
    return tempArray;
  };
  useEffect(() => {
    getUserFilter();
    defectFilterFieldStore.loadDictionaries();
    // TODO не знаю, как решить рендеринг РСО
    const cb = async () => {
      await nsi.getOrganizations();
      setUpdateKey(updateKey + 1);
    };
    cb();
    return () => {
      // Очищаем поля, фильтр, ключи отображаемых полей
      defectFilterFieldStore.visibleFieldsKeys.setKeys([]);
      defectFilterFieldStore.tempVisibleFieldsKeys.setKeys([]);
      defectFilterFieldStore.setFilterItems({});
    };
  }, []);

  useEffect(() => {}, [dictionaryStore.data]);

  const getUserFilter = async () => {
    let data =
      (await userSettingsStore.loadSetting(DEFAULT_SETTING_NAME)) || [];
    // Заглушка, чтобы всегда отображались фиксированные поля
    data = [...mainFields.map(({ name }) => name), ...data].filter(
      (value, index, self) => {
        return self.indexOf(value) === index;
      },
    );
    defectFilterFieldStore.visibleFieldsKeys.setKeys(data);
    defectFilterFieldStore.tempVisibleFieldsKeys.setKeys(data);
  };

  const saveFilter = () => {
    userSettingsStore.saveSetting(
      DEFAULT_SETTING_NAME,
      JSON.stringify(defectFilterFieldStore.visibleFieldsKeys.keys),
    );
  };

  const resetFilter = () => {
    reset(DEFAULT_VALUES, { keepDefaultValues: true });
    applyFilter(getValues());
  };

  const applyFilter = (values) => {
    saveFilters(values);
    // TODO переделать даты
    if (values.deadlineViolated) {
      values.dateTimePlanned = {
        value: moment().format("YYYY-MM-DD"),
        conditionType: FilterCondition.Less,
      };
      delete values.deadlineViolated;
    }
    if (values.dateTimeDisable) {
      if (values.dateTimeDisable[0] && values.dateTimeDisable[1]) {
        values.dateTimeDisable = {
          value: values.dateTimeDisable[0]?.format("YYYY-MM-DD"),
          valueTo: values.dateTimeDisable[1]?.format("YYYY-MM-DD"),
          conditionType: FilterCondition.Between,
        };
      } else if (values.dateTimeDisable[0] && !values.dateTimeDisable[1]) {
        values.dateTimeDisable = {
          value: values.dateTimeDisable[0]?.format("YYYY-MM-DD"),
          conditionType: FilterCondition.Equal,
        };
      } else if (!values.dateTimeDisable[0] && values.dateTimeDisable[1]) {
        values.dateTimeDisable = {
          value: values.dateTimeDisable[1]?.format("YYYY-MM-DD"),
          conditionType: FilterCondition.Equal,
        };
      }
    }
    if (values.dateTimeEnable) {
      if (values.dateTimeEnable[0] && !values.dateTimeEnable[1]) {
        values.dateTimeEnable = {
          value: values.dateTimeEnable[0]?.format("YYYY-MM-DD"),
          conditionType: FilterCondition.Equal,
        };
      } else if (values.dateTimeEnable[0] && values.dateTimeEnable[1]) {
        values.dateTimeEnable = {
          value: values.dateTimeEnable[0]?.format("YYYY-MM-DD"),
          valueTo: values.dateTimeEnable[1]?.format("YYYY-MM-DD"),
          conditionType: FilterCondition.Between,
        };
      } else if (!values.dateTimeEnable[0] && values.dateTimeEnable[1]) {
        values.dateTimeEnable = {
          value: values.dateTimeEnable[1]?.format("YYYY-MM-DD"),
          conditionType: FilterCondition.Equal,
        };
      }
    }

    if (values.id) {
      values.id = {
        value: Number(values.id),
        conditionType: FilterCondition.Like,
      };
    }

    values.addressText = {
      value: values.addressText,
      conditionType: FilterCondition.Like,
    };
    defectFilterFieldStore.setFilterItems(values);
  };

  const toggleOpenFilter = () => {
    setOpenFilters(!openFilters);
  };

  const ref = useRef(null);

  const handleDeactive = () => {
    setOpenFilters(false);
  };

  useOnClickOutside(ref, handleDeactive);

  const applyFields = () => {
    defectFilterFieldStore.visibleFieldsKeys.setKeys([
      ...defectFilterFieldStore.tempVisibleFieldsKeys.keys,
    ]);
    // Очищаем поля, которые скрыли
    fields
      .filter(
        (filterField) =>
          !defectFilterFieldStore.visibleFieldsKeys.keys.includes(
            filterField.name,
          ),
      )
      .forEach((field) => {
        setValue(field.name, DEFAULT_VALUES[field.name]);
      });
    saveFilter();

    setOpenFilters(false);
  };

  const defectFilterHead = useRef(null);

  return (
    <form onSubmit={handleSubmit(applyFilter)}>
      <div
        ref={defectFilterHead}
        className={classnames("defect__filter", styles.Filter)}
      >
        <div className="filter__fields">
          {mainFields?.map((filterField) => {
            return (
              <Controller
                key={`${filterField.name}${updateKey}`}
                control={control}
                name={filterField.name}
                render={filterField.component}
              />
            );
          })}
        </div>
        <div className="filter__controls">
          <Button
            className="custom-button"
            size="large"
            block
            type="primary"
            htmlType="submit"
          >
            Найти
          </Button>
          <Button
            onClick={resetFilter}
            className="custom-button custom-button--outline"
            size="large"
            block
            type="primary"
          >
            Сбросить
          </Button>
          <div className={styles.filters}>
            <ITooltip title={settingsFilter} placement="right">
              <FiltersIco onClick={toggleOpenFilter} />
            </ITooltip>
            {openFilters && (
              <DraggableModal
                visible={openFilters}
                defaultPosition={{ x: 530, y: 0 }}
                title={"Добавить поля фильтров"}
                footer={
                  <IButton onClick={applyFields}>Добавить фильтры</IButton>
                }
                onCancel={toggleOpenFilter}
              >
                <FieldsFilterSelector
                  fieldsKeys={defectFilterFieldStore.tempVisibleFieldsKeys}
                  unCheckableFields={mainFields}
                  checkableFields={fields}
                />
              </DraggableModal>
            )}
          </div>
        </div>
      </div>
      {groupFields().map((group) => (
        <div
          key={group.map((field) => field.name).join("")}
          className={classnames("defect__filter", styles.Filter)}
        >
          <div className="filter__fields">
            {group.map((filterField) => {
              return (
                <Controller
                  key={filterField.name}
                  control={control}
                  name={filterField.name}
                  render={filterField.component}
                />
              );
            })}
          </div>
        </div>
      ))}
    </form>
  );
});

export default DefectFilter;
